import classNames from 'classnames';
import gsap from 'gsap';
import CustomEase from 'gsap/CustomEase';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
import ScrollTrigger from 'gsap/ScrollTrigger';
import React, { MutableRefObject, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import NavBar from '../../../components/AppBar/NavBar';
import { CarouselController } from '../../../components/Carousel';
import SubscriptionUpsellModal from '../../../components/SubscriptionUpsellModal';
import HideChatWidget from '../../../components/ZendeskChat/HideChatWidget';
import useSubscriptionProducts from '../../../hooks/useSubscriptionProducts';
import * as events from '../../../lib/analytics/events';
import { getFiGrowthBook, useIsGrowthBookEnabled } from '../../../lib/growthbook';
import { colorAndDesignVariantName, sizeName } from '../../../lib/product';
import { commonDetailsForSubscription } from '../../../lib/subscription';
import * as types from '../../../types';
import { AppState, series3CollarId } from '../../../types';
import styles from '../ProductDetails.module.scss';
import BandVariantOptions from '../components/BandVariantOptions';
import SoldOutBanner from '../components/SoldOutBanner';
import useIsSkuBlocked from '../hooks/useIsSkuBlocked';
import useSubscriptionSelector from '../hooks/useSubscriptionSelector';
import useVariantSelector, { getNanoVariantOptions } from '../hooks/useVariantSelector';
import CollarAddToBagButton from './components/CollarAddToBagButton';
import FAQ from './components/FAQ';
import { DesktopImageGallery } from './components/ImageGallery';
import {
  getNanoVariantPetAgeMonths,
  getNanoVariantPetName,
  getNanoVariantPetGender,
  showNanoVariant,
} from '../../../components/NanoBanner/NanoBanner';
import NanoProductDetails from './components/NanoProductDetails/NanoProductDetails';
import { petContextActions } from '../../../reducers/series3UpgradePetContext';
import Loading from '../../../components/Loading';
import CollarButtonScrollExperiment from './components/CollarButtonScrollExperiment';
import PlusProductSummary from './components/PlusProductSummary';
import PlusAllFeatures from './components/PlusAllFeatures/PlusAllFeatures';
import PlusNewFeatures from './components/PlusNewFeatures';
import ProductSummary from './components/ProductSummary/ProductSummary';
import ReviewsCarousel from '../../../components/ReviewsCarousel/ReviewsCarousel';
import AppPaths from '../../../AppPaths';
import { useHistory } from 'react-router-dom';
import { TFunction } from 'i18next';
import { collarProductIsSupportedInCountry, useLocale } from '../../../contexts/LocaleContext';
import { SaleBadge } from '../../../components/SaleBadge/SaleBadge';
import { CustomerIO } from '../../../lib/analytics/customerIO';
import { usePdpImagesContext } from '../../../contexts/PdpImagesContext';
import useIsReferralCheckoutV2 from '../../../hooks/useIsReferralCheckoutV2';
import StarRating from '../../../components/StarRating/StarRating';
import OutcomesChecklist from '../../../components/OutcomesChecklist/OutcomesChecklist';
import useFreeTrialSubscriptionPrice from '../../../hooks/useFreeTrialSubscriptionPrice';
import { centsToDollarsNumber, getLocalizedPricing } from '../../../lib/util';
import {
  getAnalyticsProductPriceInCents,
  getAnalyticsSubscriptionValueInCents,
} from '../../../lib/analytics/analyticsUtils';
import useFreeTrialPromoVisible from '../../../hooks/useFreeTrialPromoVisible';
import useFreeTrialEligibility from '../../../hooks/useFreeTrialEligibility';
import { captureQuizReferralFromUrl, getQuizReferral, QUIZ_PRODUCT_S3_PLUS } from '../../../lib/quizReferral';

export const COLLAR_TARIFF_FEE_SKU = 'collar-tariff-50';

const SubscriptionOptions = React.lazy(() => import('../../../components/SubscriptionOptions'));
const MembershipValueProps = React.lazy(() => import('./components/MembershipValueProps'));
const PlusMembershipValueProps = React.lazy(() => import('./components/PlusMembershipValueProps'));

gsap.registerPlugin(ScrollToPlugin);
gsap.registerPlugin(CustomEase);
gsap.registerPlugin(ScrollTrigger);
CustomEase.create('scrollTo', '0.65, 0, 0.35, 1');

interface OnChangeHandlerProps {
  desktopCarouselControllerRef: React.MutableRefObject<CarouselController | undefined>;
  priceInCents: number;
  productName: string;
  subscriptionPriceInCents?: number;
  currentCarouselImage: number;
  isUpgradePath: boolean;
  automatedScrollingExperimentOn?: boolean;
  scrollToMembershipSelection?: () => void;
  currency: string;
}

export function useOnChangeHandlers({
  desktopCarouselControllerRef,
  priceInCents,
  productName,
  subscriptionPriceInCents,
  currentCarouselImage,
  isUpgradePath,
  automatedScrollingExperimentOn = false,
  scrollToMembershipSelection = () => null,
  currency,
}: OnChangeHandlerProps) {
  // For analytics events, since we're viewing an S3 collar + subscription membership bundle, we should add the
  // subscription price to the collar price
  const totalPriceInCents = useMemo(
    () => priceInCents + (subscriptionPriceInCents ?? 0),
    [priceInCents, subscriptionPriceInCents],
  );

  const onColorChange = useCallback(
    (newVariant: types.IVariant) => {
      events.series3CollarColorSwitch({
        color: newVariant.options.color,
        priceInCents: totalPriceInCents,
        productName,
        size: newVariant.options.size,
        sku: newVariant.sku,
        isUpgrade: isUpgradePath,
        currency,
      });

      // Reset the carousel to the first image if the user selects a new color
      // Do not reset the carousel if the user is on a collar image that reflects the color change
      if (currentCarouselImage > 2) {
        desktopCarouselControllerRef.current?.select(0);
      }
    },
    [totalPriceInCents, productName, isUpgradePath, currentCarouselImage, desktopCarouselControllerRef, currency],
  );

  const onSizeChange = useCallback(
    (newVariant: types.IVariant) => {
      if (automatedScrollingExperimentOn) {
        scrollToMembershipSelection();
      }
      events.series3CollarSizeSwitch({
        color: newVariant.options.color,
        priceInCents: totalPriceInCents,
        productName,
        size: newVariant.options.size,
        sku: newVariant.sku,
        isUpgrade: isUpgradePath,
        currency,
      });
    },
    [
      isUpgradePath,
      productName,
      totalPriceInCents,
      automatedScrollingExperimentOn,
      scrollToMembershipSelection,
      currency,
    ],
  );

  const onSelectedVariantChange = useCallback(
    (newVariant: types.IVariant) => {
      events.series3CollarVariantSwitch({
        color: newVariant.options.color,
        priceInCents: totalPriceInCents,
        productName,
        size: newVariant.options.size,
        sku: newVariant.sku,
        isUpgrade: isUpgradePath,
        currency,
      });
    },
    [isUpgradePath, productName, totalPriceInCents, currency],
  );

  return {
    onColorChange,
    onSizeChange,
    onSelectedVariantChange,
  };
}

const ctaButtonText = (
  hasUpdatedSize: boolean,
  hasUpdatedSubscriptionSku: boolean,
  selectedSubscription: types.ISubscriptionProduct | undefined,
  t: TFunction,
  isReferralV2 = false,
  referrerName?: string,
): string => {
  const nanoVariantEnabled = showNanoVariant();
  const petName = getNanoVariantPetName();

  if (!hasUpdatedSize) {
    return nanoVariantEnabled ? t('pick_a_size') : t('pick_your_size');
  }

  if (!hasUpdatedSubscriptionSku) {
    return t('pick_your_membership');
  }

  if (isReferralV2) {
    return referrerName ? t('referral_v2_join_on_fi', { referrerName }) : t('referral_v2_join_fi');
  }

  return nanoVariantEnabled ? t('purchase_for_pet_name', { petName }) : t('add_to_cart');
};

const ctaDescriptionText = (
  selectedVariant: types.IVariant,
  selectedSubscription: types.ISubscriptionProduct | undefined,
  redemptionType: types.RedemptionType | null,
  siteConfig: types.IECommerceSiteConfig,
  t: TFunction,
): string => {
  const gwpFreeTrialMonths = siteConfig.gwpFreeTrialMonths;

  let description =
    colorAndDesignVariantName(selectedVariant.options.color) + ' · ' + sizeName(selectedVariant.options.size);
  if (selectedSubscription) {
    description +=
      ' · ' +
      (redemptionType === 'gwp_free_trial' && gwpFreeTrialMonths
        ? t('months_free_with_count', { months: gwpFreeTrialMonths })
        : commonDetailsForSubscription(selectedSubscription).subscriptionName);
  }
  return description;
};

interface Series3CollarDetailsProps {
  addBundleCartItem: (variantSku: string, subscriptionSku: string, addonSkus?: string[]) => void;
  availableSubscriptions?: types.ISubscriptionProduct[];
  hideSubscriptionOptions?: boolean;
  productName: string;
  preselectedSubscriptionSku?: string;
  priceInCents: number;
  variants: types.IVariant[];
  isUpgradePath?: boolean;
  collarId?: string;
  redemptionType?: types.RedemptionType;
  hasValidRedemptionCode?: boolean;
}

export default function Series3CollarDetails({
  addBundleCartItem,
  availableSubscriptions,
  hideSubscriptionOptions,
  productName,
  preselectedSubscriptionSku,
  priceInCents,
  variants,
  isUpgradePath = false,
  collarId = series3CollarId,
  redemptionType = null,
  hasValidRedemptionCode = false,
}: Series3CollarDetailsProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const desktopCarouselControllerRef = useRef<CarouselController>();
  const previousCarouselIndexRef = useRef(0);
  const history = useHistory();
  const { country, currency, currencyFormatter } = useLocale();

  // Get pre-loaded dynamic images from context
  const { getMedia } = usePdpImagesContext();
  const dynamicImages = getMedia(types.series3PlusCollarId);

  const referralCode = useSelector((state: AppState) => state.storeShop.cart.referralCode);
  const referrerName = useSelector((state: AppState) => state.storeShop.cart.referrerName);
  const isReferralV2 = useIsReferralCheckoutV2();
  const siteConfig = useSelector((state: AppState) => state.config.siteConfig);
  const nanoVariantEnabled = showNanoVariant();
  const petName = getNanoVariantPetName();
  const petPronoun = getNanoVariantPetGender() === 'male' ? 'his' : 'her';
  const nanoSizeSuggestionEnabled =
    nanoVariantEnabled && getFiGrowthBook().getFeatureValue('nano-registration-size-suggestion-experiment', false);
  const automatedScrollingExperimentOn = getFiGrowthBook().getFeatureValue('automated-scroll-collar-pdp', false);

  const sizePickerRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const subscriptionPickerRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const addToCartButtonRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

  const [nanoSizeSuggestion, setNanoSizeSuggestion] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      if (nanoSizeSuggestionEnabled) {
        const options = await getNanoVariantOptions();
        setNanoSizeSuggestion(options.size);
      }
    })();
  }, [nanoSizeSuggestionEnabled]);

  useEffect(() => {
    captureQuizReferralFromUrl();
  }, []);
  const quizReferral = getQuizReferral();
  const isQuizReferredPdp =
    !isUpgradePath &&
    quizReferral?.recommendedProduct === QUIZ_PRODUCT_S3_PLUS &&
    collarId === types.series3PlusCollarId;
  const quizRecommendedSize = isQuizReferredPdp ? quizReferral?.sizeBand : undefined;
  const quizRecommendedBadgeLabel =
    isQuizReferredPdp && quizRecommendedSize
      ? quizReferral?.petName
        ? t('quiz_recommended_size_for_pet', { petName: quizReferral.petName })
        : t('quiz_recommended_size')
      : undefined;

  dispatch(petContextActions.setIsWebView(isUpgradePath));

  getFiGrowthBook().getFeatureValue('nano-ecom-test-1', false); // We want to expose this feature from the PDP page

  // A/B test for a design that more prominently displays the total amount due for the subscription term
  const prominentPricingDesign =
    isUpgradePath || nanoVariantEnabled
      ? false
      : getFiGrowthBook().getFeatureValue<boolean>('ecom-prominent-pricing', false);

  // Can be used to display a coupon-based pill above the prepaid plans
  const couponCodePillDisplay = useMemo((): { shouldShow: boolean; title?: string; subtitle?: string } => {
    return {
      shouldShow: false,
    };
  }, []);

  const allSeries3Subscriptions = useSubscriptionProducts(collarId);
  const [showUpsellModal, setShowUpsellModal] = useState<boolean>(false);
  const [currentCarouselImage, setCurrentCarouselImage] = useState<number>(0);
  const aiBehaviorRef = useRef<HTMLDivElement | null>(null);
  const specsRef = useRef<HTMLDivElement | null>(null);
  const featuresRef = useRef<HTMLDivElement | null>(null);
  const faqRef = useRef<HTMLDivElement | null>(null);
  const reviewsRef = useRef<HTMLDivElement | null>(null);

  const subscriptionProducts: types.ISubscriptionProduct[] = useMemo(
    () => availableSubscriptions ?? allSeries3Subscriptions,
    [allSeries3Subscriptions, availableSubscriptions],
  );

  const { isEligible: isFreeTrialEligible } = useFreeTrialEligibility(collarId);
  const referralFreeTrialSku =
    isReferralV2 && isFreeTrialEligible ? subscriptionProducts.find((s) => s.freeTrial)?.sku : undefined;

  // Quiz trial CTA continuity: preselect the 12-month free-trial plan the quiz offered.
  const freeTrialPlans = subscriptionProducts.filter((s) => s.freeTrial);
  const quizTrialFreeTrialSku =
    !isUpgradePath && quizReferral?.ctaType === 'trial' && isFreeTrialEligible
      ? (freeTrialPlans.find((s) => s.renewalMonths === 12) ?? freeTrialPlans[0])?.sku
      : undefined;

  preselectedSubscriptionSku = nanoSizeSuggestionEnabled
    ? subscriptionProducts.find((s) => s.renewalMonths === 6)?.sku
    : (referralFreeTrialSku ?? quizTrialFreeTrialSku ?? preselectedSubscriptionSku);

  const { selectedSubscription, selectSubscriptionSku, hasUpdatedSubscriptionSku } = useSubscriptionSelector(
    subscriptionProducts,
    preselectedSubscriptionSku,
    nanoSizeSuggestionEnabled,
  );

  const selectedSubscriptionPriceInCents = useMemo(() => {
    if (!selectedSubscription) {
      return undefined;
    }
    return getAnalyticsProductPriceInCents(selectedSubscription, currency);
  }, [selectedSubscription, currency]);

  const shouldShowSubscriptionOptions = useMemo(() => {
    if (!selectedSubscription) {
      return true;
    }

    return !hideSubscriptionOptions;
  }, [selectedSubscription, hideSubscriptionOptions]);

  // A subscription product that we should try to upsell to the user
  const subscriptionToUpsell: types.ISubscriptionProduct | undefined = useMemo(() => {
    if (!selectedSubscription) {
      return undefined;
    }

    const planCodeToUpsell = selectedSubscription.attemptUpsellToPlanCode;
    if (!planCodeToUpsell) {
      return undefined;
    }

    return subscriptionProducts.find((p) => p.sku === planCodeToUpsell);
  }, [selectedSubscription, subscriptionProducts]);

  const freeTrialPromoVisible = useFreeTrialPromoVisible();
  const freeTrialEnabled = freeTrialPromoVisible && subscriptionProducts.some((p) => p.freeTrial);
  const freeTrialSubscriptionPrice = useFreeTrialSubscriptionPrice(collarId);

  const scrollToSizeSelection = useCallback(() => {
    const offsetY = Array.from(document.querySelectorAll('.promotionBanner')).reduce((total, el) => {
      const element = el as HTMLElement;
      const computedZIndex = window.getComputedStyle(element).zIndex;
      return total + (computedZIndex === '-1' ? 0 : element.offsetHeight);
    }, 0);

    if (sizePickerRef.current) {
      gsap.to(window, {
        duration: 1.2,
        scrollTo: { y: sizePickerRef.current, offsetY: offsetY },
        ease: 'scrollTo',
      });
    }
  }, [sizePickerRef]);

  const scrollToMembershipSelection = useCallback(() => {
    const offsetY = Array.from(document.querySelectorAll('.promotionBanner')).reduce((total, el) => {
      const element = el as HTMLElement;
      const computedZIndex = window.getComputedStyle(element).zIndex;
      return total + (computedZIndex === '-1' ? 0 : element.offsetHeight);
    }, 0);

    if (subscriptionPickerRef.current) {
      ScrollTrigger.refresh();
      gsap.to(window, {
        duration: 1.2,
        scrollTo: { y: subscriptionPickerRef.current, offsetY },
        ease: 'scrollTo',
        onComplete: () => {
          if (preselectedSubscriptionSku && !hasUpdatedSubscriptionSku) {
            selectSubscriptionSku(selectedSubscription?.sku);
          }
        },
      });
    }
  }, [
    subscriptionPickerRef,
    selectSubscriptionSku,
    hasUpdatedSubscriptionSku,
    preselectedSubscriptionSku,
    selectedSubscription,
  ]);

  const scrollToAddToCart = useCallback(() => {
    const offsetYFromBanners = Array.from(document.querySelectorAll('.promotionBanner')).reduce((total, el) => {
      const element = el as HTMLElement;
      const computedZIndex = window.getComputedStyle(element).zIndex;
      return total + (computedZIndex === '-1' ? 0 : element.offsetHeight);
    }, 0);

    setTimeout(() => {
      if (addToCartButtonRef.current) {
        const rect = addToCartButtonRef.current.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const elementY = rect.top + scrollTop;
        const elementHeight = rect.height;
        const targetY = elementY - window.innerHeight / 2 + elementHeight / 2;

        gsap.to(window, {
          duration: 1, // 1 second was chosen because the sticky add to cart button appears after 1.5 seconds. This way, it doesn't look glitchy
          scrollTo: { y: targetY - offsetYFromBanners },
          ease: 'scrollTo',
        });
      }
    }, 100);
  }, [addToCartButtonRef]);

  const { onColorChange, onSizeChange, onSelectedVariantChange } = useOnChangeHandlers({
    desktopCarouselControllerRef,
    priceInCents,
    productName,
    subscriptionPriceInCents: selectedSubscriptionPriceInCents,
    currentCarouselImage,
    isUpgradePath,
    automatedScrollingExperimentOn,
    scrollToMembershipSelection,
    currency,
  });

  const { changeVariantOptions, selectedVariant, hasUpdatedSize } = useVariantSelector({
    onColorChange,
    onSelectedVariantChange,
    onSizeChange,
    variants,
    useQueryParams: true,
    automatedScrollingExperimentOn,
  });
  const isBlocked = useIsSkuBlocked(selectedVariant.sku);

  const onSubscriptionChange = useCallback(
    (subscriptionSku: string | undefined) => {
      selectSubscriptionSku(subscriptionSku);

      // When the user selects a different subscription, fire a new view content analytics event since
      // the value of the full bundle has changed
      if (!subscriptionSku) {
        return;
      }

      const subscriptionProduct = subscriptionProducts.find((p) => p.sku === subscriptionSku);
      if (!subscriptionProduct) {
        return;
      }

      // If experimenting automated scrolling behavior, scroll to add to cart button when a subscription
      // is chosen only on desktop. Skip scroll for Canada (free trial) since sticky footer is always visible.
      if (automatedScrollingExperimentOn && !freeTrialEnabled) {
        scrollToAddToCart();
      }

      const totalPriceInCents = priceInCents + getAnalyticsProductPriceInCents(subscriptionProduct, currency);
      events.series3CollarVariantSwitch({
        color: selectedVariant.options.color,
        priceInCents: totalPriceInCents,
        productName,
        size: selectedVariant.options.size,
        sku: selectedVariant.sku,
        isUpgrade: isUpgradePath,
        currency,
        analyticsSubscriptionValueInCents: getAnalyticsSubscriptionValueInCents(subscriptionProduct, currency),
      });
    },
    [
      isUpgradePath,
      priceInCents,
      productName,
      selectSubscriptionSku,
      selectedVariant.options.color,
      selectedVariant.options.size,
      currency,
      selectedVariant.sku,
      subscriptionProducts,
      automatedScrollingExperimentOn,
      scrollToAddToCart,
      freeTrialEnabled,
    ],
  );

  // Analytics - S3 collar page viewed
  useEffect(() => {
    events.series3CollarViewed();
  }, []);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    const observeOnce = (element: Element | null, onIntersect: () => void) => {
      if (!element) return;
      const observer = new IntersectionObserver(
        (entries, obs) => {
          const isVisible = entries.some((e) => e.isIntersecting);
          if (isVisible) {
            onIntersect();
            obs.disconnect();
          }
        },
        { threshold: 0.25 },
      );
      observer.observe(element);
      observers.push(observer);
    };

    observeOnce(aiBehaviorRef.current, () => events.series3CollarViewedAIBehaviorMonitoring());
    observeOnce(specsRef.current, () => events.series3CollarViewedSpecifications());
    observeOnce(featuresRef.current, () => events.series3CollarViewedFeatures());
    observeOnce(faqRef.current, () => events.series3CollarViewedFAQ());
    observeOnce(reviewsRef.current, () => events.series3CollarViewedReviews());

    return () => {
      observers.forEach((o) => o.disconnect());
    };
  }, []);

  const collarTariffFeeActive = useSelector((state: AppState) => !!state.config.siteConfig.collarTariffFeeActive);
  // Add to bag logic
  const addToBag = useCallback(
    (variant: types.IVariant, subscriptionProduct: types.ISubscriptionProduct) => {
      const subscriptionPriceInCents = getAnalyticsProductPriceInCents(subscriptionProduct, currency);

      events.series3CollarAddedToBag({
        color: variant.options.color,
        priceInCents,
        productName,
        size: variant.options.size,
        sku: variant.sku,
        isUpgrade: isUpgradePath,
        subscriptionPrice: subscriptionPriceInCents,
        analyticsSubscriptionValueInCents: getAnalyticsSubscriptionValueInCents(subscriptionProduct, currency),
        subscriptionRenewalMonths: subscriptionProduct.renewalMonths,
        currency,
      });
      events.series3CollarActionButtonClick({
        color: variant.options.color,
        priceInCents,
        productName,
        size: variant.options.size,
        sku: variant.sku,
        isUpgrade: isUpgradePath,
        currency,
      });

      events.series3SubscriptionAddedToBag({
        priceInCents: subscriptionPriceInCents,
        productId: subscriptionProduct.id,
        productName: subscriptionProduct.name,
        sku: subscriptionProduct.sku,
        isUpgrade: isUpgradePath,
        currency,
      });

      const addOnSkus: string[] = [];
      if (collarTariffFeeActive) {
        addOnSkus.push(COLLAR_TARIFF_FEE_SKU);
      }

      CustomerIO.trackAddedItemToCart(types.series3PlusCollarId, variant.options.color);
      addBundleCartItem(variant.sku, subscriptionProduct.sku, addOnSkus);
    },
    [addBundleCartItem, isUpgradePath, priceInCents, productName, collarTariffFeeActive, currency],
  );

  setTimeout(() => {
    const triggerEl = document.querySelector('.subscription-options .selected-chooser-option');
    if (triggerEl) {
      ScrollTrigger.create({
        trigger: triggerEl,
        start: 'top center',
        onEnter: () => {
          if (hasUpdatedSize && preselectedSubscriptionSku && !hasUpdatedSubscriptionSku) {
            selectSubscriptionSku(selectedSubscription?.sku);
          }
        },
      });
    }
  }, 100);

  const onAddToBagClicked = useCallback(() => {
    if (!hasUpdatedSize) {
      scrollToSizeSelection();
      return false;
    }

    if (isBlocked || !selectedSubscription || !hasUpdatedSubscriptionSku) {
      scrollToMembershipSelection();
      return false;
    }

    if (subscriptionToUpsell) {
      setShowUpsellModal(true);
      return false;
    }

    addToBag(selectedVariant, selectedSubscription);
    return true;
  }, [
    hasUpdatedSize,
    isBlocked,
    selectedSubscription,
    hasUpdatedSubscriptionSku,
    subscriptionToUpsell,
    addToBag,
    selectedVariant,
    scrollToMembershipSelection,
    scrollToSizeSelection,
  ]);

  // Handler for when user clicks "Start free trial" in the modal - adds to cart
  const handleFreeTrialAddToCart = useCallback(
    (subscriptionSku: string) => {
      const subscription = subscriptionProducts.find((product) => product.sku === subscriptionSku);
      if (subscription && selectedVariant) {
        addToBag(selectedVariant, subscription);
      }
    },
    [subscriptionProducts, selectedVariant, addToBag],
  );

  const seriesThreePlusExperience = useIsGrowthBookEnabled()
    ? getFiGrowthBook().getFeatureValue<boolean>('series-three-plus-experience', false)
    : false;

  const fiProductTitleComponent = seriesThreePlusExperience ? (
    <div className={styles.productHeader}>
      <div className={styles.productTitle}>{t('series_3_plus')}</div>
      <SaleBadge className={styles.saleBadge} freeTrialEnabled={freeTrialEnabled} />
    </div>
  ) : (
    <div className={styles.productTitle}>{t('fi_series_3')}</div>
  );

  const fiProductSubTitleComponent = seriesThreePlusExperience ? (
    <div className={styles.productTitleDescription}>
      {isQuizReferredPdp && quizReferral?.petName
        ? t('quiz_s3plus_subtitle_for_pet', { petName: quizReferral.petName })
        : t('the_worlds_most_advanced_gps_collar_built_with_ai_to_track_your_dogs_health')}
    </div>
  ) : (
    <div className={styles.productTitleDescription}>{t('pick_your_color_to_get_started')}</div>
  );

  return (
    <>
      {selectedSubscription && subscriptionToUpsell && (
        <SubscriptionUpsellModal
          onAccept={() => addToBag(selectedVariant, subscriptionToUpsell)}
          onClose={() => setShowUpsellModal(false)}
          onIgnore={() => addToBag(selectedVariant, selectedSubscription)}
          open={showUpsellModal}
          selectedSubscription={selectedSubscription}
          subscriptionToUpsell={subscriptionToUpsell}
          collarColor={selectedVariant.options.color}
        />
      )}
      <div
        className={classNames(styles.productContainer, styles.ecomFlowRevamp, 'ecom-flow-revamp', {
          'ecom-nano-variant': nanoVariantEnabled,
          'ecom-flow-revamp-light': nanoVariantEnabled
            ? selectedVariant.options.color === types.ColorOption.Blue ||
              selectedVariant.options.color === types.ColorOption.Pink ||
              currentCarouselImage === 2
            : currentCarouselImage > 0,
        })}
      >
        {!isUpgradePath && <NavBar />}
        <div
          className={classNames('image-gallery-container', styles.galleryContainer, {
            [styles.nanoGalleryContainer]: nanoVariantEnabled,
          })}
        >
          <DesktopImageGallery
            carouselControllerRef={desktopCarouselControllerRef}
            selectedVariant={selectedVariant}
            onChange={(idx) => {
              if (idx !== previousCarouselIndexRef.current) {
                const direction = idx > previousCarouselIndexRef.current ? 'next' : 'prev';
                events.series3CollarCarouselScrolled(idx, direction);
                previousCarouselIndexRef.current = idx;
              }
              setCurrentCarouselImage(idx);
            }}
            dynamicImages={dynamicImages}
          />
        </div>
        <div className={classNames(styles.productDetails, 'product-details')}>
          <div className={styles.productTitleContainer}>
            {nanoVariantEnabled && (
              <>
                <div className={styles.nanoProductTitle}>{t('fi_smart_collar')}</div>
                <div className={styles.nanoProductDescription}>
                  {t(
                    'microchips_are_a_great_start_but_they_cant_tell_you_where_your_dog_is_get_the_fi_collar_for_24_7_live_location_access',
                  )}
                </div>
              </>
            )}
            {!nanoVariantEnabled && isReferralV2 && <StarRating />}
            {!nanoVariantEnabled && fiProductTitleComponent}
            {!nanoVariantEnabled && fiProductSubTitleComponent}
            {!nanoVariantEnabled && isReferralV2 && <OutcomesChecklist />}
            {nanoVariantEnabled && (
              <div className={styles.nanoProductTitleDescription}>{t('choose_a_color_for_pet_name', { petName })}</div>
            )}
          </div>
          <BandVariantOptions
            bandSeries={types.BandSeries.F3}
            changeVariantOptions={changeVariantOptions}
            selectedVariant={selectedVariant}
            variants={variants}
            sizePickerRef={sizePickerRef}
            startSizeAsUnselected={!hasUpdatedSize}
            hideMakersLink={true}
            showColorTitle={false}
            selectedColorLabelPosition="bottom"
            sizeTitle={
              nanoSizeSuggestion
                ? getNanoVariantPetAgeMonths() <= 6
                  ? t('we_recommend_a_size_collar_for_your_puppy', { size: nanoSizeSuggestion.toString() })
                  : t('we_recommend_a_size_collar', { size: nanoSizeSuggestion.toString() })
                : nanoVariantEnabled
                  ? t('pick_pronoun_size', { pronoun: petPronoun })
                  : t('pick_your_size_title')
            }
            recommendedSize={quizRecommendedSize}
            recommendedBadgeLabel={quizRecommendedBadgeLabel}
          />

          {isBlocked && <SoldOutBanner />}

          <HideChatWidget />

          {shouldShowSubscriptionOptions && (
            <Suspense fallback={<Loading />}>
              <SubscriptionOptions
                allowNoSubscription={false}
                onSubscriptionChange={onSubscriptionChange}
                selectedSubscriptionSku={selectedSubscription?.sku}
                subscriptionProducts={subscriptionProducts}
                title={
                  nanoVariantEnabled ? t('pick_the_membership_that_works_best_for_you') : t('pick_your_membership')
                }
                subtitle={t('every_new_membership_includes_a_free_fi_series_3_plus')}
                showReferralPill={!!referralCode && !isReferralV2}
                couponPillDisplay={couponCodePillDisplay}
                productName={t('series_3_uppercase')}
                productCategory={types.ProductCategory.COLLAR_KIT}
                subscriptionPickerRef={subscriptionPickerRef}
                startAsUnselected={!preselectedSubscriptionSku && !nanoSizeSuggestionEnabled}
                redemptionType={redemptionType}
                hasValidRedemptionCode={hasValidRedemptionCode}
                disableSection={!hasUpdatedSize}
                prominentPricingDesign={prominentPricingDesign}
                onFreeTrialAddToCart={handleFreeTrialAddToCart}
                collarProductId={collarId}
              />
            </Suspense>
          )}
          <Suspense fallback={<Loading />}>
            {seriesThreePlusExperience ? (
              <PlusMembershipValueProps
                freeTrialEnabled={freeTrialEnabled}
                redemptionType={redemptionType}
                preselectedSubscription={preselectedSubscriptionSku ? selectedSubscription : undefined}
              />
            ) : (
              <MembershipValueProps />
            )}
          </Suspense>
          {!showUpsellModal &&
            (automatedScrollingExperimentOn ? (
              <CollarButtonScrollExperiment
                addToCartRef={addToCartButtonRef}
                disabled={isBlocked || !selectedSubscription || !hasUpdatedSize || !hasUpdatedSubscriptionSku}
                onAddToBag={onAddToBagClicked}
                ctaText={ctaButtonText(
                  hasUpdatedSize,
                  hasUpdatedSubscriptionSku,
                  selectedSubscription,
                  t,
                  isReferralV2,
                  referrerName,
                )}
                hasUpdatedSize={hasUpdatedSize}
                hasUpdatedSubscriptionSku={hasUpdatedSubscriptionSku}
                scrollToSizeSelection={scrollToSizeSelection}
                scrollToMembershipSelection={scrollToMembershipSelection}
                selectionSummary={
                  hasUpdatedSize && hasUpdatedSubscriptionSku
                    ? ctaDescriptionText(selectedVariant, selectedSubscription, redemptionType, siteConfig, t)
                    : undefined
                }
              />
            ) : (
              <CollarAddToBagButton
                disabled={isBlocked || !selectedSubscription}
                onAddToBag={onAddToBagClicked}
                ctaText={ctaButtonText(
                  hasUpdatedSize,
                  hasUpdatedSubscriptionSku,
                  selectedSubscription,
                  t,
                  isReferralV2,
                  referrerName,
                )}
                fadeInRef={!hasUpdatedSize ? sizePickerRef : undefined}
                isVisibleOverride={true}
                descriptionText={
                  hasUpdatedSize && hasUpdatedSubscriptionSku
                    ? ctaDescriptionText(selectedVariant, selectedSubscription, redemptionType, siteConfig, t)
                    : null
                }
                subtitleText={
                  isReferralV2 && selectedSubscription
                    ? selectedSubscription.freeTrial && freeTrialSubscriptionPrice
                      ? t('referral_v2_renews_yearly_after_trial', { price: freeTrialSubscriptionPrice })
                      : t('referral_v2_renews_at_price_per_period', {
                          price: currencyFormatter.format(
                            centsToDollarsNumber(getLocalizedPricing(selectedSubscription.pricingInCents, currency)),
                          ),
                          period: t(`referral_v2_period_${selectedSubscription.renewalMonths}`),
                        })
                    : selectedSubscription?.freeTrial && freeTrialSubscriptionPrice
                      ? t('membership_after_trial', { price: freeTrialSubscriptionPrice })
                      : null
                }
              />
            ))}
        </div>
      </div>
      {nanoVariantEnabled && <NanoProductDetails productType="collar" />}
      {seriesThreePlusExperience ? (
        <>
          <PlusNewFeatures containerRef={aiBehaviorRef} />
          <PlusProductSummary containerRef={specsRef} />
          <PlusAllFeatures containerRef={featuresRef} freeTrialEnabled={freeTrialEnabled} />
          {collarProductIsSupportedInCountry(types.miniCollarId, country) && (
            <div className={styles.compareAllProductsButton} onClick={() => history.push(AppPaths.Compare)}>
              {t('compare_all_products')}
            </div>
          )}
        </>
      ) : (
        <ProductSummary containerRef={specsRef} />
      )}
      <FAQ containerRef={faqRef} freeTrialEnabled={freeTrialEnabled} collarId={collarId} />
      <div ref={reviewsRef}>
        <ReviewsCarousel />
      </div>
    </>
  );
}
